import React, { useEffect, useState } from 'react';

import { useNavigate, useParams } from 'react-router-dom';

import { Grid, Stack } from '@mui/material';
import useCoupons from 'api/useCoupons';
import useOffers from 'api/useOffers';
import {
  Coupon,
  EPublisherSolutionModel,
  Offer,
  PromoCode
} from 'common/contracts';
import { EButtonColor, ELocalStorageKeys, OfferType } from 'constants/enums';
import dayjs from 'dayjs';
import { FormikErrors, FormikTouched, useFormik } from 'formik';
import { localStorageUtil } from 'utils/localStorageUtil';
import * as yup from 'yup';

import AcContentWrapper from '../../../components/AcContentWrapper/AcContentWrapper';
import AcViewWrapper from '../../../components/AcViewWrapper/AcViewWrapper';
import DialogModal from '../../../components/Dialog/Dialog';
import PageTopBar from '../../../components/Topbar/PageTopBar';
import { EToasterStatus, showToast } from '../../../utils/showToast';
import { ICouponsFormProps, UpsertCouponWithPromoCodeDto } from '../types';
import {
  checkUniqueness,
  generateCouponDto,
  generatePromoCodesDto,
  getOffersForSelect
} from '../utils';

import CouponsGeneralSettings from './components/CouponsGeneralSettings';
import CouponsOffersSettings from './components/CouponsOffersSettings';
import CouponsPlayerSettings from './components/CouponsPlayersSettings/CouponsPlayerSettings';
import CouponsPromoCodes from './components/CouponsPromoCodes';

import '../../../style/forms.scss';

export const CouponsForm: React.FC<ICouponsFormProps> = ({
  edit = false,
  dup = false
}) => {
  const { publisherId } = useParams();
  const navigate = useNavigate();
  const { couponId } = useParams();

  const { getSingleCoupon, upsertCoupon, isPromoCodeActive } = useCoupons(
    publisherId,
    couponId
  );
  const { getCoupons } = useCoupons(publisherId);

  const [neverExpires, setNeverExpires] = useState(
    getSingleCoupon.data?.expiredBy === null
  );

  const [isUnSavedChangesModalOpen, setIsUnSavedChangesModalOpen] =
    useState(false);

  const [promoCodes, setPromoCodes] = useState<
    {
      _id: string;
      name: string;
      maxRedemptions: number;
      creationDate: string;
      active: boolean;
    }[]
  >([]);
  const [promoCode, setPromoCode] = useState('');
  const [maxRedemptions, setMaxRedemptions] = useState<string | number>('');

  useEffect(() => {
    setPromoCodes(
      (getSingleCoupon.data?.promoCodes as any)?.map((promoCode: any) => ({
        _id: promoCode.id || '',
        name: promoCode.name || '',
        maxRedemptions: promoCode.maxRedemptions || 0,
        active: promoCode.active || false
      }))
    );
  }, [getSingleCoupon.data]);

  const { getOffers } = useOffers({
    offerType: OfferType.BUNDLE,
    fetchOnlyActiveOffers: true,
    customPagination: { currentPage: 0, rowsPerPage: 500 }
  });

  const { getOffers: getSpecialOffers } = useOffers({
    offerType: OfferType.SPECIAL_OFFER,
    fetchOnlyActiveOffers: true,
    customPagination: { currentPage: 0, rowsPerPage: 500 }
  });

  const { getOffers: getPopupOffers } = useOffers({
    offerType: OfferType.POPUP,
    fetchOnlyActiveOffers: true,
    customPagination: { currentPage: 0, rowsPerPage: 500 }
  });

  const offersMap = {
    bundles: getOffers.data || [],
    promotions: getSpecialOffers.data || [],
    popups: getPopupOffers.data || []
  };

  const isCheckoutUser =
    localStorageUtil.get(ELocalStorageKeys.PUBLISHER_SOLUTION_MODEL) ===
    EPublisherSolutionModel.CHECKOUT;

  const supportedOffersBySku = isCheckoutUser
    ? []
    : getSingleCoupon.data?.supportedOffersBySku ??
      getOffersForSelect(
        offersMap,
        getSingleCoupon.data?.supportedOffersByType || []
      )?.map((offer) => offer.key);

  let couponSchema = yup.object().shape({
    name: yup
      .string()
      .required('Coupon name is required')
      .test('uniqueness', 'Coupon name already exists', (value) => {
        return checkUniqueness(
          value,
          getCoupons.data,
          getSingleCoupon.data?.name,
          edit
        );
      }),
    active: yup.boolean().required('Coupon status is required'),
    discountPercentage: yup
      .number()
      .integer('Discount percentage must be a whole number')
      .min(1, 'Discount percentage must be at least 1')
      .max(99, 'Discount percentage must be at most 99')
      .required('Discount percentage is required'),
    maxRedemptionsPerCustomer: yup
      .number()
      .min(0, 'Max redemptions per customer must be greater than or equal to 1')
      .notRequired(),
    expiredBy: yup
      .date()
      .nullable()
      .min(dayjs(), 'Expires on date should be in the future')
      .when('neverExpires', {
        is: false,
        then: yup
          .date()
          .required('Expiry date is required when not set to never expire')
      }),
    firstTimePurchase: yup.boolean(),
    ...(isCheckoutUser
      ? {}
      : {
          supportedOffersBySku: yup.array().of(yup.string())
        }),
    promoCodes: yup
      .array()
      .of(
        yup.object().shape({
          name: yup.string().required('Promo code name is required'),
          maxRedemptions: yup
            .number()
            .min(0, 'Max redemptions must be greater than or equal to 0')
            .required('Max redemptions is required')
        })
      )
      .min(1, 'At least one promo code must be added')
  });

  const {
    values,
    handleChange,
    handleBlur,
    submitForm,
    errors,
    dirty,
    isSubmitting,
    touched,
    isValid,
    setFieldValue,
    setFieldTouched
  } = useFormik({
    validateOnMount: true,
    validationSchema: couponSchema,
    enableReinitialize: true,
    initialValues: {
      name: getSingleCoupon.data?.name || '',
      active: getSingleCoupon.data?.active ?? true,
      discountPercentage: getSingleCoupon.data?.discountPercentage || '',
      maxRedemptionsPerCustomer:
        getSingleCoupon.data?.maxRedemptionsPerCustomer || '',
      expiredBy: getSingleCoupon.data?.expiredBy || null,
      firstTimePurchase: getSingleCoupon.data?.firstTimePurchase || false,
      supportedOffersBySku,
      promoCodes: getSingleCoupon.data?.promoCodes || [],
      allowedPlayers: getSingleCoupon.data?.allowedPlayers || []
    },
    onSubmit: async (values, helpers) => {
      const promoCodesData = generatePromoCodesDto(values.promoCodes, couponId);
      const couponData = generateCouponDto(values, neverExpires);

      const upsertCouponData: UpsertCouponWithPromoCodeDto = {
        coupon: couponData,
        promoCodes: promoCodesData
      };

      const isEdit = Boolean(edit);

      const onSuccess = async () => {
        if (edit) {
          await getSingleCoupon.refetch();
        }
        if (getSingleCoupon.data) {
          helpers.setValues({
            ...getSingleCoupon.data,
            promoCodes: (getSingleCoupon.data.promoCodes || []).map(
              (promoCode) => ({
                ...promoCode,
                fresh: false
              })
            )
          });
        }
        showToast({
          message: `Coupon ${isEdit ? 'updated' : 'created'} successfully`,
          status: EToasterStatus.SUCCESS
        });

        if (!isEdit) {
          navigate(-1);
        }
      };

      const onError = (error: any) => {
        const serverError = error?.response?.data?.message;
        showToast({
          message:
            serverError || `Error ${isEdit ? 'updating' : 'creating'} coupon`,
          status: EToasterStatus.ERROR
        });
      };

      upsertCoupon.mutate(upsertCouponData, {
        onSuccess,
        onError
      });
    }
  });

  const isSaveEnabled = dirty && isValid && !upsertCoupon.isLoading;

  const getOffersForSelectBound = (supportedOffersByType?: string[]) =>
    getOffersForSelect(
      {
        bundles: getOffers.data?.offers,
        promotions: getSpecialOffers.data?.offers,
        popups: getPopupOffers.data?.offers
      },
      supportedOffersByType
    );

  return (
    <>
      <AcViewWrapper
        header={
          <PageTopBar
            disable={false}
            headline={`${edit ? 'Edit' : 'New'} Coupon ${
              edit
                ? `(${
                    !getSingleCoupon.isLoading && getSingleCoupon.data
                      ? getSingleCoupon.data.name
                      : '...'
                  })`
                : ''
            }`}
            buttons={[
              {
                text: 'Save',
                action: () => submitForm(),
                disabled: !isSaveEnabled
              }
            ]}
            backFunction={() => {
              if (isSaveEnabled) {
                setIsUnSavedChangesModalOpen(true);
              } else {
                navigate(-1);
              }
            }}
          />
        }
      >
        <AcContentWrapper className="formContent">
          <Grid container pt={'24px'}>
            <Grid item xs={12}>
              <Stack>
                <CouponsGeneralSettings
                  couponFormValues={values}
                  edit={edit}
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                  errors={errors as FormikErrors<Coupon>}
                  touched={touched as FormikTouched<Coupon>}
                  setFieldTouched={setFieldTouched}
                  setFieldValue={setFieldValue}
                  neverExpires={neverExpires}
                  dup={dup}
                  updatedAt={getSingleCoupon.data?.updatedAt}
                  setNeverExpires={setNeverExpires}
                />

                {!isCheckoutUser && (
                  <CouponsOffersSettings
                    supportedOffersBySku={values.supportedOffersBySku}
                    getOffersForSelect={getOffersForSelectBound}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    errors={errors as FormikErrors<Coupon>}
                    touched={touched as FormikTouched<Coupon>}
                    setFieldValue={setFieldValue}
                  />
                )}

                <CouponsPlayerSettings
                  couponFormValues={values}
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                  errors={errors as FormikErrors<Coupon>}
                  touched={touched as FormikTouched<Coupon>}
                  setFieldValue={setFieldValue}
                  dup={dup}
                />
                <CouponsPromoCodes
                  couponFormValues={values}
                  handleBlur={handleBlur}
                  setFieldValue={setFieldValue}
                  promoCode={promoCode}
                  setPromoCode={setPromoCode}
                  maxRedemptions={maxRedemptions}
                  setMaxRedemptions={setMaxRedemptions}
                  isPromoCodeActive={isPromoCodeActive}
                />
              </Stack>
            </Grid>
          </Grid>
        </AcContentWrapper>
      </AcViewWrapper>

      <DialogModal
        isOpen={isUnSavedChangesModalOpen}
        headline="Unsaved Changes"
        text="You changed a promo code but didn't save the coupon. Leaving will lose your changes."
        buttons={[
          {
            text: 'Proceed',
            color: EButtonColor.ERROR,
            variant: 'outlined',
            func: () => {
              navigate(-1);
            }
          },
          {
            text: 'Save Coupon',
            color: EButtonColor.SECONDARY,
            variant: 'contained',
            func: () => {
              submitForm();
              setIsUnSavedChangesModalOpen(false);
            }
          }
        ]}
        closeDialog={() => setIsUnSavedChangesModalOpen(false)}
      />
    </>
  );
};
